import React from "react";
import LegalShell, { LegalSection } from "./LegalShell";
import { LEGAL } from "../../lib/legalConfig";

/**
 * /legal/terms — Terms of Service.
 *
 * ⚠️ LAWYER-APPROVED COPY (5 February 2026). Do NOT edit the prose without
 * going back to counsel and flipping LEGAL.REVIEWED=false during the
 * revision cycle.
 */
export default function TermsPage() {
  return (
    <LegalShell
      title="Terms of Service"
      effectiveDate={LEGAL.TERMS_EFFECTIVE_DATE}
      lastUpdated={LEGAL.TERMS_EFFECTIVE_DATE}
      testid="legal-terms"
      intro={
        <>
          These Terms govern your use of Xchangely. By creating an account or
          placing an order you agree to them. If you do not agree, please do
          not use the service.
        </>
      }
    >
      <LegalSection id="who-we-are" n="1" title="Who we are">
        <p>
          Xchangely is a virtual-currency on-ramp ("Xchangely", "we", "us").
          You can reach us at support@xchangely.io.
        </p>
      </LegalSection>

      <LegalSection id="what-it-does" n="2" title="What Xchangely does">
        <p>
          Xchangely lets eligible customers buy crypto-assets (currently
          stablecoins) using a bank transfer in supported currencies. To do
          this we work with a regulated payment partner that verifies your
          identity, provides you with deposit details, and processes your
          payment. Once your payment is confirmed, we deliver the
          crypto-asset you purchased to the wallet address you have provided
          and approved.
        </p>
      </LegalSection>

      <LegalSection id="eligibility" n="3" title="Eligibility">
        <p>
          To use Xchangely you must: be at least 18 years old; be a resident
          of a country we support (the list is shown during onboarding and
          may change); not be a resident of a jurisdiction we are unable to
          serve; not appear on any applicable sanctions or prohibited-persons
          list; and use the service only for your own account and lawful
          purposes. We do not currently serve residents of Quebec. We may
          refuse, suspend or close access where eligibility is not met.
        </p>
      </LegalSection>

      <LegalSection id="identity-verification" n="4" title="Identity verification and your payment partner">
        <p>
          Before you can transact, you must complete identity verification.
          Verification, the creation of your deposit account details (a
          virtual IBAN), and the processing of your bank payment are{" "}
          <strong>
            facilitated through our regulated payment partner
          </strong>
          , and you may be directed to that partner's secure hosted page to
          verify your identity and obtain your deposit instructions. You
          agree to provide accurate, current and complete information, and to
          provide additional information or documentation (including
          source-of-funds information) where we are required to request it.
          We may decline or delay a transaction pending verification or
          review.
        </p>
      </LegalSection>

      <LegalSection id="pricing" n="5" title="Quotes, pricing and fees">
        <p>
          Prices for crypto-assets are volatile. Any price or quote shown is
          indicative until your transaction is confirmed, and is valid only
          for the limited period shown. The total cost of your purchase —
          including the exchange rate applied and our fee — is presented to
          you before you commit. By proceeding you accept the total shown.
        </p>
      </LegalSection>

      <LegalSection id="delivery" n="6" title="Delivery of crypto-assets and wallet addresses">
        <p>
          You are solely responsible for providing a correct destination
          wallet address <strong>and selecting the correct network</strong>{" "}
          for the crypto-asset you are buying.{" "}
          <strong>Crypto-asset transactions are irreversible.</strong>{" "}
          Crypto-assets sent to a wrong address, or sent on the wrong
          network, cannot be recovered by us or anyone else, and we are not
          liable for losses resulting from an incorrect address or network
          you provided or approved. For your protection, destination wallets
          must be added and approved before they can be used.
        </p>
      </LegalSection>

      <LegalSection id="completion-times" n="7" title="Completion times">
        <p>
          We aim to deliver purchased crypto-assets promptly after your
          payment is confirmed. Some orders — for example larger purchases
          or first-time customers — may require additional verification and
          take longer. We do not guarantee a specific completion time.
        </p>
      </LegalSection>

      <LegalSection id="refunds" n="8" title="Refunds and returns">
        <p>
          If a purchase cannot be completed — for example, because
          verification is not passed or a payment cannot be matched — we
          will return the relevant funds to the originating bank account
          through our payment partner, less any unavoidable third-party
          costs, in accordance with applicable law. Because crypto-asset
          deliveries are irreversible, a completed delivery cannot be
          reversed.
        </p>
      </LegalSection>

      <LegalSection id="prohibited" n="9" title="Prohibited use">
        <p>
          You must not use Xchangely for any unlawful purpose, to launder
          money or finance terrorism, to evade sanctions, on behalf of an
          undisclosed third party, or in breach of these Terms. We monitor
          for and report suspicious activity as required by law, and we may
          freeze, decline or report activity without notice where the law
          requires.
        </p>
      </LegalSection>

      <LegalSection id="risk" n="10" title="Risk disclosure">
        <p>
          Crypto-assets are volatile and carry risk. Xchangely does not
          provide investment, tax or legal advice, and nothing on the site
          is a recommendation to buy or sell any asset. You are responsible
          for your own decisions and for any tax arising from your
          transactions.
        </p>
      </LegalSection>

      <LegalSection id="availability" n="11" title="Availability and changes">
        <p>
          We may modify, suspend or discontinue any part of the service, and
          we may update these Terms. Material changes will be notified
          through the site or by email; continued use after changes take
          effect constitutes acceptance.
        </p>
      </LegalSection>

      <LegalSection id="liability" n="12" title="Liability">
        <p>
          To the maximum extent permitted by law, Xchangely is not
          liable for indirect or consequential losses, for losses arising
          from incorrect wallet/network details you provided, for
          crypto-asset price movements, or for delays or failures of third
          parties (including banks, payment partners and blockchain
          networks). Nothing limits liability that cannot be limited by law.
        </p>
      </LegalSection>

      <LegalSection id="law" n="13" title="Governing law">
        <p>
          These Terms are governed by the laws of the Province of British
          Columbia and the federal laws of Canada applicable there, and the
          courts of British Columbia have jurisdiction, subject to any
          mandatory consumer-protection rights in your country of residence.
        </p>
      </LegalSection>

      <LegalSection id="contact" n="14" title="Contact">
        <p>
          Questions about these Terms: support@xchangely.io.
        </p>
      </LegalSection>
    </LegalShell>
  );
}
